import React from "react";

import { _isMobile } from "../commonFunctions";
import { normalizeSourceType, NOTIFICATION_TABS } from "./NotificationPanel";

import "./NotificationPanel.css";

const priorityColor = {
    HIGH: "#FF0000",
    MEDIUM: "rgb(255 136 0 / 50%)",
    LOW: "#00FF00",
};

function hexToRgba(hex, alpha = 1) {
    const raw = String(hex || "").replace("#", "");
    if (raw.length !== 6) return `rgba(255, 255, 255, ${alpha})`;
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function priorityToAlpha(priority) {
    const p = String(priority || "LOW").toUpperCase();
    if (p === "HIGH") return 1;
    if (p === "MEDIUM") return 0.78;
    return 0.58;
}

const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = Math.max(0, now - (timestamp || 0));

    const minutes = Math.floor(diff / (60 * 1000));
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

export default function NotificationItem({ item, handleNotificationClick, handleActionButtonClick, dismissToast, is_toast }) {
    const tabKey = normalizeSourceType(item.source_type);
    const tab = NOTIFICATION_TABS[tabKey] || NOTIFICATION_TABS.all;
    const baseBg = "#ffffff" || tab.backgroundColor || tab.background;
    const hoverBg = tab.hoverColor || baseBg;
    const svgColor = tab.svgColor || tab.avatarColor || "#cccccc";

    const alpha = priorityToAlpha(item.priority);
    const backgroundColor = !item.is_read ? "#E3EDF9" : hexToRgba(baseBg, 1);
    const hoverBackgroundColor = hexToRgba(hoverBg, Math.max(1, alpha + 0.12));

    return (
        <article
            key={item.id}
            className={`notification-panel-item notification-panel-item--priority-${String(item.priority || "LOW").toLowerCase()} ${item.primary_action ? "cuP" : ""} pR`}
            style={{
                "--notification-panel-bg": backgroundColor,
                "--notification-panel-hover-bg": hoverBackgroundColor,
                "--notification-panel-border-color": item.is_read ? "#FFFFFF" : svgColor,
            }}
            onClick={() => {
                handleNotificationClick(item);
                dismissToast && dismissToast();
            }}
        >
            {is_toast && !_isMobile() && (
                <div
                    className="notification-panel-toast-dismiss-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        dismissToast && dismissToast();
                    }}
                >
                    <button type="button" className="notification-panel-toast-dismiss-button-inner cuP">
                        &times;
                    </button>
                </div>
            )}

            <div className="notification-panel-avatar-wrapper">
                <div className={`notification-panel-avatar notification-panel-avatar--${String(item.priority || "LOW").toLowerCase()}`} style={{ backgroundColor: svgColor }}>
                    <span className="notification-panel-avatar-icon">{tab.svg ? React.cloneElement(tab.svg, { style: { fill: "#111827" } }) : React.cloneElement(NOTIFICATION_TABS.all.svg, { style: { fill: "#111827" } })}</span>
                </div>
            </div>

            <div className="notification-panel-item-body">
                <div className="notification-panel-item-header">
                    <div className="notification-panel-item-header-main">
                        <div className="notification-panel-item-title">
                            <span className="notification-panel-item-name">{item.heading}</span>
                        </div>
                        <div className="notification-panel-item-meta">
                            <span>{formatTimeAgo(item.timestamp)}</span>
                            <span className="notification-panel-dot-separator">•</span>
                            <span>{tab.label}</span>
                            {item.priority && item.priority !== "LOW" && <span className={`notification-panel-status-dot notification-panel-fade-in-out`} style={{ backgroundColor: priorityColor[item.priority] }}></span>}
                        </div>
                    </div>
                </div>

                {item.text && <p className="notification-panel-message">{item.text}</p>}

                {item.actions && item.actions.length > 0 && (
                    <div className="notification-panel-item-actions">
                        {item.actions
                            .filter((action) => action.displayType === "BUTTON")
                            .sort((a, b) => (a.position === "SECONDARY" ? -1 : 1))
                            .map((action) => (
                                <button
                                    key={action.id}
                                    type="button"
                                    className={`notification-panel-action-button ${action.position === "SECONDARY" ? "notification-panel-action-button--secondary" : "notification-panel-action-button--tertiary"}`}
                                    onClick={(e) => {
                                        handleActionButtonClick(e, action, item);
                                        dismissToast && dismissToast();
                                    }}
                                >
                                    {action.label || (action.position === "SECONDARY" ? "Accept" : "Decline")}
                                </button>
                            ))}
                    </div>
                )}
            </div>
        </article>
    );
}
